// `ls-ctrl` DataChannel（component/webrtc/spec/03-ls-ctrl.md）——這裡只用它開關對講。
//
// 為什麼一定要有：lsrtc 設備在收到 `{"op":"select","talk":true}` 之前，會把 App 送來的音訊直接丟掉
// （CTL-044；component/webrtc/docs/10-interop-log.md「對講要宿主明確開啟」）。只把麥克風打開，
// 在 Kinio 原生設備上是聽不到的。iOS（KinioAPI/KinioCalls.swift `KinioLsCtrl`）、Android
// （kiniocore KinioLsCtrl.kt / KinioTalkControl）同一套；plan/device-call-v0.md §2。
//
// 設備沒實作 ls-ctrl（v3 KVS 設備、pion emulator 只會 echo）→ 永遠等不到 `hello`，
// 對講照舊只靠麥克風（CTL-001：ls-ctrl 是可選的加值，媒體不依賴它）。

export const LS_CTRL_LABEL = 'ls-ctrl';
export const LS_CTRL_PROTOCOL = 'ls-ctrl.v1';

export type LsCtrlMessage =
  | { kind: 'hello'; talkCapable: boolean }
  | { kind: 'response'; id: number | null; ok: boolean; err: string | null }
  | { kind: 'state'; reason: string | null; talk: boolean | null };

/** `{"id":…,"op":"select","talk":…}`（鍵序固定，跟 iOS / Android 一樣）。 */
export function lsCtrlSelect(id: number, talk: boolean): string {
  return `{"id":${id},"op":"select","talk":${talk}}`;
}

/** 解一個 text frame。不是 response / event（例：emulator 把 request 原樣 echo 回來）→ null。 */
export function parseLsCtrl(text: string): LsCtrlMessage | null {
  let o: Record<string, unknown>;
  try {
    const v: unknown = JSON.parse(text);
    if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
    o = v as Record<string, unknown>;
  } catch {
    return null;
  }
  const ev = typeof o.ev === 'string' ? o.ev : '';
  if (ev) {
    if (ev === 'hello') {
      const caps = o.caps && typeof o.caps === 'object' ? (o.caps as Record<string, unknown>) : {};
      return { kind: 'hello', talkCapable: caps.talk === true };
    }
    if (ev === 'state') {
      const st = o.state && typeof o.state === 'object' ? (o.state as Record<string, unknown>) : null;
      return {
        kind: 'state',
        reason: typeof o.reason === 'string' && o.reason ? o.reason : null,
        talk: st && typeof st.talk === 'boolean' ? st.talk : null,
      };
    }
    return null;
  }
  if (!('op' in o) && 'ok' in o) {
    return {
      kind: 'response',
      id: typeof o.id === 'number' ? o.id : null,
      ok: o.ok === true,
      err: typeof o.err === 'string' && o.err ? o.err : null,
    };
  }
  return null;
}

/**
 * 對講開關的狀態機：hello 之前先記著想要的狀態，hello 到了（且設備有對講）再送；
 * 設備拒絕（`TALK_BUSY`：同一時間只允許一個觀看者對講，CTL-044）→ onRejected。
 * send 回 false = channel 沒開，留著等下一次 hello / setTalk。
 */
export class TalkControl {
  onRejected: (err: string) => void = () => undefined;
  /** 設備 hello 到了（`caps.talk`）：即時頁用來停用對講並說明。 */
  onHello: (talkCapable: boolean) => void = () => undefined;
  private nextId = 1;
  private helloReceived = false;
  private capable = false;
  private want: boolean | null = null;
  private pendingId: number | null = null;

  constructor(private readonly send: (frame: string) => boolean) {}

  /** hello 之後才知道：null = 還沒收到 hello（可能設備沒有 ls-ctrl）。 */
  get talkCapable(): boolean | null {
    return this.helloReceived ? this.capable : null;
  }

  setTalk(on: boolean): void {
    this.want = on;
    this.flush();
  }

  onMessage(text: string): void {
    const m = parseLsCtrl(text);
    if (!m) return;
    if (m.kind === 'hello') {
      this.helloReceived = true;
      this.capable = m.talkCapable;
      this.onHello(m.talkCapable);
      this.flush();
    } else if (m.kind === 'response') {
      const mine = m.id !== null && m.id === this.pendingId;
      if (mine) this.pendingId = null;
      if (mine && !m.ok) this.onRejected(m.err ?? 'error');
    }
  }

  private flush(): void {
    const want = this.want;
    if (!this.helloReceived || !this.capable || want === null) return;
    const id = this.nextId++;
    this.pendingId = id;
    this.want = null;
    if (!this.send(lsCtrlSelect(id, want))) {
      // channel 還沒開：把狀態放回去，下一次觸發再送。
      if (this.want === null) this.want = want;
    }
  }
}
